<template>
  <div v-if="controls" class="card-rail-frame">
    <div
      ref="railEl"
      class="card-rail card-rail--controls"
      :class="railClasses"
      role="region"
      tabindex="0"
      :aria-label="label"
      v-bind="$attrs"
    >
      <slot />
    </div>
    <button
      v-if="canPrev"
      type="button"
      class="card-rail__nav card-rail__nav--prev"
      data-testid="rail-prev"
      :aria-label="prevLabel"
      @click="page(-1)"
    >
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
        <path d="M15.4 7.4 14 6l-6 6 6 6 1.4-1.4-4.6-4.6z" fill="currentColor" />
      </svg>
    </button>
    <button
      v-if="canNext"
      type="button"
      class="card-rail__nav card-rail__nav--next"
      data-testid="rail-next"
      :aria-label="nextLabel"
      @click="page(1)"
    >
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
        <path d="M8.6 16.6 10 18l6-6-6-6-1.4 1.4 4.6 4.6z" fill="currentColor" />
      </svg>
    </button>
  </div>
  <div
    v-else
    ref="railEl"
    class="card-rail"
    :class="railClasses"
    role="region"
    tabindex="0"
    :aria-label="label"
    v-bind="$attrs"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRailPointerNav } from '~/composables/useRailPointerNav';

/**
 * DESIGN.md "Horizontal card rail" (cas-edf2; Ben Q31 B, Q42): a swipeable row
 * of cards whose peek is exactly the next card's inline padding, so the sliver
 * shows the next card's edge and surface and never its text. Cards snap to
 * start. There is no fade and no mask. The rail is a focusable, labelled region
 * so the keyboard can scroll it.
 *
 * `tiles` is DESIGN.md "Media tile rail" (cas-e1b5): N whole media tiles (2, 3
 * from Quasar's sm breakpoint, 4 from md) and a --space-10 peek of the next
 * tile's thumbnail. A tile that is only partly in view hides the text marked
 * `data-rail-caption` (visibility, not alpha) until it snaps fully in.
 *
 * `controls` (cas-9b1fd) makes every card reachable by pointer, not only by
 * touch and keyboard: a vertical mouse wheel over the rail scrolls it sideways
 * (and hands the wheel back to the page at either end), a mouse drag scrolls it
 * without picking the card it started on, and prev/next buttons page through
 * it on fine pointers. Still no fade or mask: the next button and the peek are
 * the "there's more" hint. With `controls` a frame wraps the scroller so the
 * buttons are not tiles; either way the scroller keeps every attribute passed
 * to the rail (inheritAttrs off, `$attrs` bound on the scroller). Keep the
 * template to one root per branch: a template comment there would make the
 * rail a fragment.
 */
defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    /** Accessible name for the rail region (usually the section title). */
    label: string;
    /** From 1024px the cards wrap into a grid instead of scrolling. */
    wrapDesktop?: boolean;
    /** Media tiles: N whole tiles plus a thumbnail-only peek. */
    tiles?: boolean;
    /** Wheel, mouse drag and prev/next buttons (cas-9b1fd). */
    controls?: boolean;
    /** Accessible name of the previous button (with `controls`). */
    prevLabel?: string;
    /** Accessible name of the next button (with `controls`). */
    nextLabel?: string;
  }>(),
  { wrapDesktop: false, tiles: false, controls: false, prevLabel: 'Previous', nextLabel: 'Next' }
);

const PARTIAL = 'data-rail-partial';
const railEl = ref<HTMLElement | null>(null);
const railClasses = computed(() => ({
  'card-rail--wrap-desktop': props.wrapDesktop,
  'card-rail--tiles': props.tiles
}));
const { canPrev, canNext, page } = useRailPointerNav(railEl, () => props.controls);
let intersection: InstanceType<typeof globalThis.IntersectionObserver> | null = null;
let mutation: InstanceType<typeof globalThis.MutationObserver> | null = null;

function observeTiles() {
  const rail = railEl.value;
  if (!rail || !intersection) return;
  intersection.disconnect();
  for (const tile of Array.from(rail.children)) intersection.observe(tile);
}

onMounted(() => {
  const rail = railEl.value;
  if (!props.tiles || !rail || typeof window.IntersectionObserver === 'undefined') return;
  intersection = new window.IntersectionObserver(
    entries => {
      for (const entry of entries)
        entry.target.toggleAttribute(PARTIAL, entry.intersectionRatio < 0.99);
    },
    { root: rail, threshold: [0, 0.99, 1] }
  );
  observeTiles();
  if (typeof window.MutationObserver !== 'undefined') {
    mutation = new window.MutationObserver(observeTiles);
    mutation.observe(rail, { childList: true });
  }
});

onBeforeUnmount(() => {
  intersection?.disconnect();
  mutation?.disconnect();
});
</script>

<style scoped lang="scss">
.card-rail {
  /* The gap between cards, and the peek: the card's own inline padding. */
  --rail-gap: var(--space-3);
  --rail-peek: var(--space-4);

  display: flex;
  gap: var(--rail-gap);
  overflow-x: auto;
  overscroll-behavior-x: contain;
  padding-bottom: var(--space-1);
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

/* cas-9b1fd: the frame holds the scroller and its prev/next buttons. */
.card-rail-frame {
  max-width: 100%;
  min-width: 0;
  position: relative;
}

/* Tile offsets are measured from the scroller (see useRailPointerNav). */
.card-rail--controls {
  position: relative;
}

.card-rail--dragging {
  cursor: grabbing;
  user-select: none;
}

/* Prev/next: icon-only round buttons, solid (Q42), on fine pointers only;
   touch swipes natively. */
.card-rail__nav {
  align-items: center;
  background: var(--g-bg-elevated);
  border: 1px solid var(--g-border-control);
  border-radius: var(--g-radius-full);
  color: var(--g-text-primary);
  cursor: pointer;
  display: none;
  height: var(--space-10);
  justify-content: center;
  padding: 0;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: var(--space-10);
  z-index: 1;
}

.card-rail__nav--prev {
  left: var(--space-1);
}

.card-rail__nav--next {
  right: var(--space-1);
}

.card-rail__nav:hover {
  background: var(--g-hover-wash);
}

.card-rail__nav:focus-visible {
  outline: 2px solid var(--g-focus-ring);
  outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
  .card-rail__nav {
    display: flex;
  }
}

.card-rail::-webkit-scrollbar {
  display: none;
}

.card-rail:focus-visible {
  outline: 2px solid var(--g-focus-ring);
  outline-offset: 2px;
}

.card-rail > :slotted(*) {
  flex: 0 0 calc(100% - var(--rail-gap) - var(--rail-peek));
  min-width: 0;
  scroll-snap-align: start;
}

/* Media tiles: N whole tiles and a --space-10 slice of the next thumbnail. */
.card-rail--tiles {
  --rail-tiles: 2;
  --rail-peek: var(--space-10);
}

.card-rail--tiles > :slotted(*) {
  flex: 0 0
    calc((100% - var(--rail-tiles) * var(--rail-gap) - var(--rail-peek)) / var(--rail-tiles));
}

@media (min-width: $breakpoint-sm-min) {
  .card-rail--tiles {
    --rail-tiles: 3;
  }
}

@media (min-width: $breakpoint-md-min) {
  .card-rail--tiles {
    --rail-tiles: 4;
  }

  .card-rail--wrap-desktop {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(18rem, 1fr));
    overflow-x: visible;
  }

  .card-rail--wrap-desktop > :slotted(*) {
    flex: none;
  }
}
</style>

<style lang="scss">
/* Unscoped on purpose: captions can sit inside a tile's own components, where
   the rail's slot scope id does not reach. Namespaced by the tiles class. */
.card-rail--tiles > [data-rail-partial] [data-rail-caption] {
  visibility: hidden;
}
</style>
